import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleOAuthProvider, GoogleLogin as GoogleLoginButton } from '@react-oauth/google';
import { Person } from '../../types';
import { profileUpdateApi } from '../../services/profileUpdateApi';
import { CandidateProfileModal } from '../People/CandidateProfileModal';
import { CvImportCard } from './CvImportCard';
import { Footer } from '../Layout/Footer';
import { ProfileUpdatePublicShell } from './ProfileUpdatePublicShell';
import { usePersonCvUpload } from '../../hooks/usePersonCvUpload';

const RAW_API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002';
const API_BASE = RAW_API_BASE.replace(/\/$/, '').endsWith('/api')
  ? RAW_API_BASE.replace(/\/$/, '')
  : `${RAW_API_BASE.replace(/\/$/, '')}/api`;
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

function getTokenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('token')?.trim() || null;
}

function getJwtFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('jwt')?.trim() || null;
}

function getErrorFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('error')?.trim() || null;
}

function isMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) || (typeof window !== 'undefined' && window.innerWidth < 768);
}

/** Mobile: GSI HTML API with redirect (no popup). Loads script once and renders button. */
function GoogleRedirectSignIn({
  clientId,
  loginUri,
  scriptLoadedRef,
}: {
  clientId: string;
  loginUri: string;
  scriptLoadedRef: React.MutableRefObject<boolean>;
}) {
  useEffect(() => {
    if (scriptLoadedRef.current) return;
    if (document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
      scriptLoadedRef.current = true;
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => { scriptLoadedRef.current = true; };
    document.body.appendChild(script);
  }, [scriptLoadedRef]);

  return (
    <>
      <div
        id="g_id_onload"
        data-client_id={clientId}
        data-login_uri={loginUri}
        data-ux_mode="redirect"
        data-auto_prompt="false"
        data-context="signin"
      />
      <div
        className="g_id_signin flex justify-center"
        data-type="standard"
        data-theme="filled_black"
        data-size="large"
        data-shape="rectangular"
        data-width="280"
        data-text="signin_with"
      />
    </>
  );
}

export function ProfileUpdateApp() {
  const [state, setState] = useState<'loading' | 'invalid' | 'signin' | 'app' | 'success'>('loading');
  const [token, setToken] = useState<string | null>(null);
  const [jwt, setJwt] = useState<string | null>(null);
  const [_personName, setPersonName] = useState<string | null>(null);
  const [isInternalProfile, setIsInternalProfile] = useState(false);
  const [linkExpiresAt, setLinkExpiresAt] = useState<string | null>(null);
  const [person, setPerson] = useState<Person | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [invalidReason, setInvalidReason] = useState<string | null>(null); // 403 = wrong account, etc.
  const [consentGiven, setConsentGiven] = useState(false);
  const gsiScriptLoaded = useRef(false);

  const tokenFromUrl = getTokenFromUrl();
  const jwtFromUrl = getJwtFromUrl();
  const errorFromUrl = getErrorFromUrl();

  // After redirect from Google (mobile): jwt and token in URL → set state and clean URL
  useEffect(() => {
    if (!jwtFromUrl || !tokenFromUrl) return;
    setToken(tokenFromUrl);
    setJwt(jwtFromUrl);
    setState('app');
    const url = new URL(window.location.href);
    url.searchParams.delete('jwt');
    url.searchParams.delete('error');
    window.history.replaceState({}, '', url.pathname + url.search);
  }, [jwtFromUrl, tokenFromUrl]);

  // URL has error param (e.g. after failed redirect callback)
  useEffect(() => {
    if (!tokenFromUrl || !errorFromUrl) return;
    if (errorFromUrl === 'missing_credential') setSubmitError('Sign-in was cancelled or failed.');
    else if (errorFromUrl === 'unauthorized') setSubmitError('This account is not allowed. Use your @creativedock.com email.');
    else if (errorFromUrl === 'auth_failed') setSubmitError('Sign-in failed. Please try again.');
    const url = new URL(window.location.href);
    url.searchParams.delete('error');
    window.history.replaceState({}, '', url.pathname + url.search);
  }, [tokenFromUrl, errorFromUrl]);

  useEffect(() => {
    if (!tokenFromUrl) {
      // No token in URL. Check if we have a standard session JWT in localStorage.
      const sessionJwt = localStorage.getItem('talentfirst_token');
      if (sessionJwt) {
        setJwt(sessionJwt);
        setState('app');
      } else {
        setState('invalid');
      }
      return;
    }
    if (jwtFromUrl) return; // let the jwt effect handle it
    setToken(tokenFromUrl);
    profileUpdateApi.validateToken(tokenFromUrl).then((r) => {
      if (r.valid) {
        setPersonName(r.personName ?? null);
        setIsInternalProfile(Boolean(r.isInternal) || r.profileCategory === 'employee');
        setLinkExpiresAt(r.expiresAt ?? null);
        setState('signin');
      } else {
        setInvalidReason(null);
        setState('invalid');
      }
    }).catch(() => {
      setInvalidReason(null);
      setState('invalid');
    });
  }, [tokenFromUrl, jwtFromUrl]);

  const handleGoogleSuccess = useCallback(async (credential: string) => {
    try {
      const { token: newJwt } = await profileUpdateApi.googleLogin(credential);
      setJwt(newJwt);
      setState('app');
    } catch (e: any) {
      console.error(e);
      setSubmitError(e.response?.data?.error || 'Sign-in failed');
    }
  }, []);

  const loadProfile = useCallback(async () => {
    if (!jwt) return;
    try {
      const p = token
        ? await profileUpdateApi.loadProfile(token, jwt)
        : await profileUpdateApi.loadSessionProfile(jwt);
      setPerson(p);
      setInvalidReason(null);
    } catch (e: unknown) {
      type AxiosLikeError = {
        response?: {
          status?: number;
          data?: { error?: string; signed_in_as?: string | null };
        };
      };
      const response = (e as AxiosLikeError | undefined)?.response;
      const status = response?.status;
      const err = response?.data?.error;
      const signedInAs = response?.data?.signed_in_as;
      if (status === 403) {
        const base =
          err || 'This link is for another account. Sign in with the email that matches this profile.';
        setInvalidReason(
          signedInAs
            ? `${base} You are currently signed in as ${signedInAs}.`
            : base
        );
      } else {
        setInvalidReason(null);
      }
      setState('invalid');
    }
  }, [token, jwt]);

  const magicViewer = React.useMemo(
    () => ({ kind: 'self_magic' as const, token: token || '', jwt: jwt || '' }),
    [token, jwt]
  );

  const cvUpload = usePersonCvUpload({
    personId: person?.id,
    viewerContext: magicViewer,
    onImported: () => void loadProfile(),
    enabled: state === 'app' && person?.id != null && !!jwt,
  });

  useEffect(() => {
    if (state !== 'app') return;
    void loadProfile();
  }, [state, loadProfile]);

  if (state === 'loading') {
    return (
      <ProfileUpdatePublicShell>
        <p className="py-12 text-center text-[#a3a3a3]">Validating link…</p>
      </ProfileUpdatePublicShell>
    );
  }

  if (state === 'invalid') {
    const isWrongAccount = !!invalidReason;
    return (
      <ProfileUpdatePublicShell>
        <div className="w-full min-w-0 rounded-2xl border border-[#2a2a2a] bg-[#1a1a1a] p-8 text-center">
          <div className="w-14 h-14 rounded-full border-2 border-[#FF4800] flex items-center justify-center mx-auto mb-4 text-2xl text-[#FF4800]">!</div>
          <h1 className="text-lg font-bold text-[#e5e5e5] mb-2">
            {isWrongAccount ? 'Wrong account' : 'Link expired or invalid'}
          </h1>
          <p className="text-sm text-[#a3a3a3]">
            {isWrongAccount ? invalidReason : 'Request a new link from your team or HR to update your profile.'}
          </p>
          {isWrongAccount && (
            <p className="text-xs text-[#6b7280] mt-3">Sign in with the Google account that matches this profile’s contact email.</p>
          )}
        </div>
      </ProfileUpdatePublicShell>
    );
  }

  if (state === 'signin') {
    const useRedirect = isMobile() && token;
    const callbackUrl = token
      ? `${API_BASE}/auth/google-profile-update-callback?token=${encodeURIComponent(token)}`
      : `${API_BASE}/auth/google-profile-update-callback`;

    return (
      <ProfileUpdatePublicShell>
        <div className="w-full min-w-0 rounded-2xl border border-[#2a2a2a] bg-[#1a1a1a] p-8">
          <h2 className="text-lg font-bold text-[#e5e5e5] text-center mb-1">Update your profile</h2>
          <p className="text-sm text-[#a3a3a3] text-center mb-4">
            {isInternalProfile
              ? 'Sign in with your @creativedock.com Google account (must match your profile email).'
              : 'Sign in with the Google account that matches your profile contact email (personal Gmail is OK).'}
          </p>
          <div className="rounded-xl border border-[#3f3f46] bg-[#141414] px-3 py-3 mb-6 text-left text-[11px] leading-relaxed text-[#b4b4b4]">
            <p className="font-semibold text-[#e5e5e5] mb-1.5">Important</p>
            <ul className="list-disc pl-4 space-y-1.5">
              <li>
                This link can be used until <strong className="text-[#e5e5e5]">you successfully save</strong> your profile—then it
                stops working (one completion). If you only opened the page, you can continue until you save or until expiry.
              </li>
              {linkExpiresAt && (
                <li>
                  Latest expiry:{' '}
                  <strong className="text-[#e5e5e5]">{new Date(linkExpiresAt).toLocaleString()}</strong>
                </li>
              )}
              <li>Use this session soon on the device/browser where you will sign in; do not leave the URL in shared chats or unsecured notes.</li>
              <li>Do not forward through insecure channels (e.g. public messengers). Copy now and paste where you need it.</li>
            </ul>
          </div>

          <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative mt-1 flex shrink-0 items-center">
                <input
                  type="checkbox"
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.target.checked)}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border border-[#3f3f46] bg-[#141414] transition-all checked:bg-[#00B074] checked:border-[#00B074]"
                />
                <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <span className="min-w-0 flex-1 text-xs text-[#a3a3a3] leading-relaxed break-words group-hover:text-[#e5e5e5] transition-colors">
                I consent to the processing of my personal data for talent management purposes within Creative Dock, as described in the{' '}
                <a href="/privacy-policy" target="_blank" className="text-[#00B074] hover:underline">Privacy Policy</a>.
              </span>
            </label>
          </div>

          {GOOGLE_CLIENT_ID ? (
            <div className={`w-full max-w-full overflow-hidden ${!consentGiven ? 'opacity-50 pointer-events-none grayscale' : ''}`}>
              {useRedirect ? (
                <GoogleRedirectSignIn
                  clientId={GOOGLE_CLIENT_ID}
                  loginUri={callbackUrl}
                  scriptLoadedRef={gsiScriptLoaded}
                />
              ) : (
                <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                  <div className="flex justify-center">
                    <GoogleLoginButton
                      onSuccess={(res) => res.credential && handleGoogleSuccess(res.credential)}
                      onError={() => setSubmitError('Google sign-in failed')}
                      theme="filled_black"
                      size="large"
                      shape="rectangular"
                      width={280}
                      text="signin_with"
                      ux_mode="popup"
                    />
                  </div>
                </GoogleOAuthProvider>
              )}
            </div>
          ) : (
            <p className="text-sm text-red-400 text-center">Google sign-in is not configured.</p>
          )}
          {submitError && <p className="mt-4 text-sm text-red-400 text-center">{submitError}</p>}
        </div>
      </ProfileUpdatePublicShell>
    );
  }

  if (state === 'success') {
    return (
      <ProfileUpdatePublicShell>
        <div className="w-full min-w-0 rounded-2xl border border-[#2a2a2a] bg-[#1a1a1a] p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-[#00B074] flex items-center justify-center mx-auto mb-4 text-white text-xl font-bold">✓</div>
          <h1 className="text-lg font-bold text-[#e5e5e5] mb-2">Profile updated</h1>
          <p className="text-sm text-[#a3a3a3]">You can close this page.</p>
          <p className="text-xs text-[#6b7280] mt-3 leading-relaxed">
            This magic link no longer works after a successful save. If you need to change your profile again, ask your admin or HR
            for a new link.
          </p>
        </div>
      </ProfileUpdatePublicShell>
    );
  }

  // app
  if (!person || !jwt) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="text-[#a3a3a3]">Loading profile…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center p-4">
      <div className="max-w-5xl w-full space-y-6">
        <CvImportCard
          cvUpload={cvUpload}
          token={token}
          jwt={jwt}
          onImported={() => {
            void loadProfile();
          }}
        />
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl overflow-hidden shadow-2xl">
          <CandidateProfileModal
            person={person}
            presentation="embedded"
            viewerContext={{ kind: 'self_magic', token: token || '', jwt }}
            cvUpload={cvUpload}
            onClose={() => {}}
            onFinalize={() => setState('success')}
            onPersonUpdated={loadProfile}
          />
        </div>
        <Footer />
      </div>
    </div>
  );
}
